import { Form, Formik } from "formik";
import { Pencil, Search, Upload } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type HousingStandard, listHousing, updateHousing } from "@/features/reference/api";
import { US_STATE_OPTIONS } from "@/features/practitioners/us-states";
import { ApiError } from "@/lib/api";
import { ImportHousingDialog } from "./import-housing-dialog";

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const money = () => Yup.string().matches(MONEY_RE, "Enter a valid amount").required("Required");
const SIZES = [1, 2, 3, 4, 5] as const;
const ALL_STATES = "__all__";

interface CountyGroup {
  state: string;
  county: string;
  bySize: Record<number, HousingStandard>;
}

function group(rows: HousingStandard[]): CountyGroup[] {
  const map = new Map<string, CountyGroup>();
  for (const r of rows) {
    const key = `${r.state}|${r.county}`;
    if (!map.has(key)) map.set(key, { state: r.state, county: r.county, bySize: {} });
    map.get(key)!.bySize[r.householdSize] = r;
  }
  return [...map.values()].sort((a, b) =>
    a.state === b.state ? a.county.localeCompare(b.county) : a.state.localeCompare(b.state),
  );
}

export function LocalHousingTab() {
  const [groups, setGroups] = useState<CountyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(ALL_STATES);
  const [countyInput, setCountyInput] = useState("");
  const [county, setCounty] = useState("");
  const [editing, setEditing] = useState<CountyGroup | undefined>();
  const [importOpen, setImportOpen] = useState(false);

  const refetch = useCallback(() => {
    setLoading(true);
    listHousing({
      state: state === ALL_STATES ? undefined : state,
      county: county || undefined,
    })
      .then((r) => setGroups(group(r.items)))
      .catch(() => toast.error("Couldn't load housing standards."))
      .finally(() => setLoading(false));
  }, [state, county]);

  useEffect(() => {
    const id = setTimeout(() => setCounty(countyInput.trim()), 300);
    return () => clearTimeout(id);
  }, [countyInput]);
  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={state} onValueChange={setState}>
          <SelectTrigger size="sm" className="w-[130px]" aria-label="State">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATES}>All states</SelectItem>
            {US_STATE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative w-full sm:w-56">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="County…"
            className="pl-9"
            value={countyInput}
            onChange={(e) => setCountyInput(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto gap-1.5"
          onClick={() => setImportOpen(true)}
        >
          <Upload className="size-4" /> Import county housing
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No counties match. Only a sample ships — use import to load the full table.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>County</TableHead>
                {SIZES.map((s) => (
                  <TableHead key={s} className="text-right">
                    {s}p
                  </TableHead>
                ))}
                <TableHead className="text-right">Edit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={`${g.state}|${g.county}`}>
                  <TableCell className="font-mono text-xs">{g.state}</TableCell>
                  <TableCell className="font-medium">{g.county}</TableCell>
                  {SIZES.map((s) => (
                    <TableCell key={s} className="text-right tabular-nums">
                      {g.bySize[s]?.monthlyAmount ?? "—"}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Edit ${g.county}`}
                      onClick={() => setEditing(g)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editing && (
        <EditCountyDialog group={editing} onClose={() => setEditing(undefined)} onSaved={refetch} />
      )}
      <ImportHousingDialog open={importOpen} onOpenChange={setImportOpen} onImported={refetch} />
    </div>
  );
}

function EditCountyDialog({
  group,
  onClose,
  onSaved,
}: {
  group: CountyGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialValues = Object.fromEntries(
    SIZES.map((s) => [`size_${s}`, group.bySize[s]?.monthlyAmount ?? ""]),
  ) as Record<string, string>;
  const schema = Yup.object(
    Object.fromEntries(SIZES.map((s) => [`size_${s}`, money()])),
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit {group.county}, {group.state}
          </DialogTitle>
          <DialogDescription>Monthly housing & utilities allowance by household size.</DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              // Each household size is its own row; PATCH only the ones that changed.
              await Promise.all(
                SIZES.map((s) => {
                  const row = group.bySize[s];
                  const val = values[`size_${s}`] ?? "";
                  if (row && row.monthlyAmount !== val) return updateHousing(row.id, val);
                  return Promise.resolve();
                }),
              );
              toast.success("County housing updated");
              onSaved();
              onClose();
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't update the county.");
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                {SIZES.map((s) => (
                  <FormField key={s} name={`size_${s}`} label={`${s}p`} inputMode="decimal" />
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
